// src/hooks/useMachines.js

import { useEffect, useState, useCallback, useRef } from "react";
import { db, onSnapshot, doc } from "../services/firestore";
import { ORG_ID, MACHINES, MACHINE_LOW_STOCK_THRESHOLD } from "../constants";
import { keyForRowLike } from "../utils/rijHelpers";   
import {
  getLoadedPhysicalRowCount,
  removePhysicalRowFromQueue,
} from "../utils/machineQueue";
import { logger } from "../utils/logger";

function getMachinePlanningSummary(rows, rijenMap) {
  const trackedRows = [];
  const seen = new Set();

  for (const row of rows || []) {
    const key = keyForRowLike(row);
    if (!key || seen.has(key)) continue;

    seen.add(key);

    const actueleRij =
      rijenMap && typeof rijenMap.get === "function" ? rijenMap.get(key) : null;

    trackedRows.push({
      key,
      koelcel: row?.koelcel ?? actueleRij?.koelcel ?? "",
      rij: row?.rij ?? actueleRij?.rij ?? "",
      count: Number(actueleRij?.huidige_bezetting || 0),
    });
  }

  const totalBoxes = trackedRows.reduce((sum, row) => sum + row.count, 0);

  return {
    rowCount: trackedRows.length,
    totalBoxes,
    trackedRows,
  };
}

// isPlanning = mag deze client auto-advance doen? (planning + uitscanner)
export default function useMachines({ rijenMap, isPlanning, enabled }) {
  const [machines, setMachines] = useState({});
  const [machineAlert, setMachineAlert] = useState(null);

  const advanceCooldown = useRef({});
  const COOLDOWN_MS = 1500;

  // History van aantallen per rij-key, zodat we overgang >0 → 0 kunnen detecteren
  const lastCountsRef = useRef({});

  // -------------------------------------------------------------
  // 1) Firestore listeners → machines state
  // -------------------------------------------------------------
  useEffect(() => {
    if (!enabled) {
      setMachines({});
      return;
    }

    const unsubscribers = [];

    MACHINES.forEach((machineId) => {
      const ref = doc(db, "orgs", ORG_ID, "machines", String(machineId));

      const unsub = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        setMachines((prev) => ({
          ...prev,
          [String(machineId)]: { ...data, machineId: String(machineId) },
        }));
      });

      unsubscribers.push(unsub);
    });

    return () => unsubscribers.forEach((u) => u());
  }, [enabled]);

  // -------------------------------------------------------------
  // 2) Head-first check → advance als head-rij net leeg is
  // -------------------------------------------------------------
  const advanceMachineIfHeadEmpty = useCallback(
    async (machineId) => {
      try {
        const m = machines[String(machineId)];
        if (!m || !m.rows || m.rows.length === 0) return;

        const headRow = m.rows[0];
        const headKey = keyForRowLike(headRow);
        const count = getLoadedPhysicalRowCount(rijenMap, headRow);

        // Een ontbrekende rij betekent dat de koelceldata op deze route niet
        // geladen is. Behandel dat nooit als een lege, uitgescande rij.
        if (count === null) return;

        // Vorige waarde ophalen (of huidige als we nog geen history hebben)
        const lastCounts = lastCountsRef.current;
        const prevCount =
          typeof lastCounts[headKey] === "number" ? lastCounts[headKey] : count;

        // Altijd bijwerken zodat we de overgang kunnen zien
        lastCounts[headKey] = count;

        // ✅ Alleen advance als deze rij van >0 naar 0 is gegaan
        //    → dus echt "net leeg gescand"
        if (!(prevCount > 0 && count === 0)) {
          return;
        }

        // Cooldown om niet te vaak te saven
        const now = Date.now();
        const last = advanceCooldown.current[machineId] || 0;
        if (now - last < COOLDOWN_MS) return;

        advanceCooldown.current[machineId] = now;

        // ⬇️ Belangrijk: head-rij NU ECHT VERWIJDEREN
        const newRows = m.rows.slice(1);

        logger.info("AUTO_ADVANCE", {
          machineId,
          prevRows: m.rows,
          newRows,
        });

        // App.js schrijft dit weg naar Firestore
        return { machineId, newRows };
      } catch (e) {
        logger.error("ADVANCE_ERROR", { machineId, e });
      }
    },
    [machines, rijenMap]
  );

  // -------------------------------------------------------------
  // 3) Helpers: addRowToMachine, removeRowFromMachine, isAssignedToMachine
  // -------------------------------------------------------------
  const addRowToMachine = useCallback(
    (machineId, rijLike) => {
      const m = machines[String(machineId)];
      if (!m) return null;

      const key = keyForRowLike(rijLike);
      if ((m.rows || []).some((r) => keyForRowLike(r) === key)) return null; // al aanwezig

      return [ ...(m.rows || []), rijLike ]; // pure array terug
    },
    [machines]
  );

  const removeRowFromMachine = useCallback(
    (machineId, rijLike) => {
      const m = machines[String(machineId)];
      if (!m) return null;

      return removePhysicalRowFromQueue(m.rows, rijLike);
    },
    [machines]
  );

  const isAssignedToMachine = useCallback(
    (machineId, rijLike) => {
      const m = machines[String(machineId)];
      if (!m) return false;

      const key = keyForRowLike(rijLike);
      return (m.rows || []).some((r) => keyForRowLike(r) === key);
    },
    [machines]
  );

  // -------------------------------------------------------------
  // 4) Niet-blokkerende waarschuwing: machineplanning bijna leeg
  // -------------------------------------------------------------
  useEffect(() => {
  if (!rijenMap) return;

  const alerts = Object.values(machines)
    .filter((m) => !!m?.machineId && Array.isArray(m.rows) && m.rows.length > 0)
    .map((m) => {
      const summary = getMachinePlanningSummary(m.rows, rijenMap);
      return {
        machineId: m.machineId,
        ...summary,
      };
    })
    .filter(
      ({ totalBoxes }) =>
        totalBoxes > 0 && totalBoxes <= MACHINE_LOW_STOCK_THRESHOLD
    )
    .sort((a, b) => a.totalBoxes - b.totalBoxes);

  if (!alerts.length) {
    setMachineAlert(null);
    return;
  }

  setMachineAlert({
    machineId: alerts[0].machineId,
    alerts,
  });
}, [rijenMap, machines]);

  // -------------------------------------------------------------
  // 5) maybeAutoAdvance – App.js roept dit aan
  // -------------------------------------------------------------
  const maybeAutoAdvance = useCallback(
    async () => {
      if (!isPlanning) return null;

      const changes = [];
      for (const machineId of Object.keys(machines)) {
        const c = await advanceMachineIfHeadEmpty(machineId);
        if (c) changes.push(c);
      }

      return changes; // App.js doet de setDoc
    },
    [machines, isPlanning, advanceMachineIfHeadEmpty]
  );

  // -------------------------------------------------------------
  // API
  // -------------------------------------------------------------
  return {
    machines,
    machineAlert,
    addRowToMachine,
    removeRowFromMachine,
    isAssignedToMachine,
    maybeAutoAdvance,
  };
}
